import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

import QuickBets from 'components/Betslip/components/QuickBets';
import RGModalMessage from 'components/Betslip/components/RGModalMessage';
import SelectedBetOddsNotificationMessage from 'components/Betslip/components/SelectedBetOddsNotificationContent/SelectedBetOddsNotificationContent';
import MobileBetslipHeader from 'components/MobilePlacement/MobileBetslipHeader/MobileBetslipHeader';
import Payout from 'components/MobilePlacement/Payout/Payout';
import PlaceButton from 'components/MobilePlacement/PlaceButton/PlaceButton';
import PlacementVirtualKeyboard from 'components/MobilePlacement/PlacementVirtualKeyboard/PlacementVirtualKeyboard';
import { CurrentBetActions, MarketStatuses, OddsTypes, PageBlocks } from 'constants/app';
import { BetsStatusesTypes } from 'constants/app';
import { MOBILE_INPUT_LIABILITY, MOBILE_INPUT_PRICE, MOBILE_INPUT_SIZE } from 'constants/automation';
import { PersistenceTypes } from 'constants/bets';
import { BetFocusFields } from 'constants/betslip';
import { mobileBetslip as branding, mobileComponents, mobileIcons } from 'constants/branding';
import { InlinePlacementActions, InlinePlacementStatuses } from 'constants/inlinePlacement';
import { BettingTypes } from 'constants/markets';
import { BetSides } from 'constants/myBets';
import {
  LOADING_TIMEOUT_MESSAGE,
  VALIDATION_ERROR_BET_INVALID_US_PRICE,
  VALIDATION_ERROR_DIFFERENT_CURRENCY_MARKET
} from 'constants/placement';
import {
  BETSLIP_BACKERS_ODDS,
  BETSLIP_BACKERS_STAKE,
  BETSLIP_BACKERS_UNITS,
  BETSLIP_LIABILITY,
  BETSLIP_ODDS,
  BETSLIP_STAKE,
  BETSLIP_UNITS
} from 'constants/tooltip';
import { useCurrency } from 'hooks/useCurrency';
import { useIsMobilePlacementDisabled } from 'hooks/useMobilePlacement';
import useMobilePlacementNotificationMatchedBySystem from 'hooks/useMobilePlacementNotificationMatchedBySystem';
import useOddsType from 'hooks/useOddsType';
import {
  getBetslipSpinnerTime,
  getCurrency,
  getDesktopSettingsDefStake,
  getIsLandscape,
  getIsSportLayOddsDisabled,
  getMobileSettingsVirtualKeyboardBetslip,
  getPNCEnabledSetting
} from 'redux/modules/appConfigs/selectors';
import { getLoggedInStatusState } from 'redux/modules/auth/selectors';
import { BetFocusField, TProfitValidation } from 'redux/modules/betslip/type';
import { BetsStatusesType } from 'redux/modules/betsStatuses/type';
import { getCurrentBetByOfferId, getCurrentBetsByOldOfferId } from 'redux/modules/currentBets/selectors';
import { TCurrentBet } from 'redux/modules/currentBets/type';
import { getCurrentGameIsLayOddsDisabled } from 'redux/modules/games/selectors';
import {
  removeInlineSelectedBet,
  setInlinePlacedSize,
  setIsMobilePlacementConfirmationStep,
  updateInlineSelectedBet
} from 'redux/modules/inlinePlacement';
import {
  getInlineSelectedBetByMarket,
  getIsMobilePlacementConfirmationStep
} from 'redux/modules/inlinePlacement/selectors';
import { TInlineSelectedBet } from 'redux/modules/inlinePlacement/type';
import { getStatusByMarketPricesId } from 'redux/modules/marketsPrices/selectors';
import { getMarketDataById, getMarketPricesById } from 'redux/modules/placement/selectors';
import {
  getAccountSettings,
  getUserAutoRefreshEnabled,
  getUserCurrency,
  getUserLoading
} from 'redux/modules/user/selectors';
import { PageBlock, PlacementPage } from 'types';
import { PersistenceType, TPrice, TProfit, TSize } from 'types/bets';
import { InlinePlacementStatus } from 'types/inlinePlacement';
import { formatBestPrice, getBestPriceFromMarketPrices, getBestPrices } from 'utils/betslip';
import { formatFloatNumber } from 'utils/betValidation';
import { calculatePayout, calculateSize } from 'utils/liability';
import { getLiabilityValue } from 'utils/mobileBetslip';
import convertOdds from 'utils/oddsConverter';
import { validateProfit } from 'utils/profit';
import { precisionFormat, validateSize } from 'utils/size';

import LiabilityInput from './Liability/LiabilityInput';
import MobilePlacementClosedMessage from './MobilePlacementClosedMessage/MobilePlacementClosedMessage';
import MobilePlacementValidationMessage from './MobilePlacementValidationMessage/MobilePlacementValidationMessage';
import NoEditableInput from './NoEditableInput/NoEditableInput';
import PersistenceRadioGroup from './PersistenceRadioGroup/PersistenceRadioGroup';
import Liability from './Liability';
import PriceInput from './PriceInput';
import SizeInput from './SizeInput';

import styles from './styles.module.scss';

interface MobilePlacementProps {
  /**
   * Bet data that consists of the market and selection information, price and size, offerId (if bet is placed)
   */
  bet: TInlineSelectedBet;

  /**
   * Place where component was added.
   */
  pageBlock?: PageBlock;
  /**
   * On closing the betslip
   */
  onClosingBetslip?: (isVisible: boolean) => void;
  page?: PlacementPage;
  containerClassName?: string;
  shouldScrollToView?: boolean;
}

const initialInvalidInputs = {
  [BetFocusFields.PRICE]: false,
  [BetFocusFields.SIZE]: false
};

function MobilePlacement({
  bet,
  pageBlock = PageBlocks.HOME,
  onClosingBetslip = () => {},
  page,
  shouldScrollToView,
  containerClassName = ''
}: MobilePlacementProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { exchangeOddsType: oddsType, isAmericanOdds } = useOddsType();
  const elRef = useRef<HTMLDivElement>(null);
  const isGame = pageBlock === PageBlocks.GAME;

  const virtualKeyboardBetslip = useSelector(getMobileSettingsVirtualKeyboardBetslip);
  const betslipSpinnerTime = useSelector(getBetslipSpinnerTime);
  const isLoggedIn = useSelector(getLoggedInStatusState);
  const loadingUser = useSelector(getUserLoading);
  const userCurrency = useSelector(getUserCurrency);
  const market = useSelector(getMarketDataById(pageBlock, bet.marketId, bet.sportId));
  const inlineSelectedBet = useSelector(getInlineSelectedBetByMarket(pageBlock, bet.marketId));
  const isPNCEnabled = useSelector(getPNCEnabledSetting);
  const currentBet = useSelector(getCurrentBetByOfferId(bet?.currentOfferId || 0));
  const currentOldBets = useSelector(getCurrentBetsByOldOfferId(bet?.currentOfferId || 0));

  const accountSettings = useSelector(getAccountSettings);
  const marketPrices = useSelector(getMarketPricesById(pageBlock, bet.marketId));
  const defaultCurrency = useSelector(getCurrency);
  const autoRefreshIsEnabled = useSelector(getUserAutoRefreshEnabled);
  const currency = useCurrency(defaultCurrency.currencyCode);
  const marketStatus = useSelector(getStatusByMarketPricesId(bet.marketId));
  const isConfirmationStep = useSelector(getIsMobilePlacementConfirmationStep);
  const isSportsDisabledLayOdds = useSelector(
    getIsSportLayOddsDisabled(bet.type === BetSides.Lay ? bet.sportId : undefined)
  );
  const isGamesLayOddsDisabled = useSelector(getCurrentGameIsLayOddsDisabled(isGame && bet.type === BetSides.Lay));
  const isLandscape = useSelector(getIsLandscape);
  const defStakeEnabled = useSelector(getDesktopSettingsDefStake);

  const selectedBet =
    inlineSelectedBet && inlineSelectedBet.offers && inlineSelectedBet?.currentOfferId
      ? inlineSelectedBet.offers[inlineSelectedBet?.currentOfferId]
      : null;

  const isLay = bet.type === BetSides.Lay;
  const isBack = bet.type === BetSides.Back;
  const bettingType = market?.description.bettingType;
  const isLineMarket = bettingType === BettingTypes.LINE;

  const [invalidInputs, setInvalidInputs] =
    useState<Record<Exclude<BetFocusField, 'LIABILITY'>, boolean>>(initialInvalidInputs);
  const [currentFocus, setCurrentFocus] = useState<BetFocusField | null>(BetFocusFields.SIZE);
  const [priceValue, setPriceValue] = useState<TPrice>(bet.price);
  const [customPriceValue, setCustomPriceValue] = useState<string | null>(null);
  const [prevPriceValue, setPrevPriceValue] = useState<TPrice>(bet.price);
  const [sizeValue, setSizeValue] = useState<TSize>(bet.size);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasErrorMessage, setHasErrorMessage] = useState(false);
  const [isPriceReady, setIsPriceReady] = useState(true);
  const [status, setStatus] = useState<InlinePlacementStatus>(InlinePlacementStatuses.NEW);
  const [placedBet, setPlacedBet] = useState<TCurrentBet | null>(selectedBet || null);
  const [isTakeOffer, setIsTakeOffer] = useState(false);
  const [persistenceType, setPersistenceType] = useState<PersistenceType>(PersistenceTypes.LAPSE);
  const [liabilityValue, setLiabilityValue] = useState<number | string | undefined>(
    isLay
      ? getLiabilityValue({
          marketId: bet.marketId,
          type: bet.type,
          handicap: bet.handicap,
          selectionId: bet.selectionId,
          priceValue,
          sizeValue,
          marketDefinition: marketPrices?.marketDefinition,
          eachWayDivisor: bet.eachWayDivisor
        })
      : undefined
  );
  const [profit, setProfit] = useState<TProfit>(null);

  const [isSizeValid, setIsSizeValid] = useState(
    validateSize({
      size: bet.size,
      betType: bet.type,
      currency,
      defaultCurrency,
      currencyCode: bet.currency,
      isOperatorBettingLimitsEnabled: false,
      isGame
    }).isValid
  );

  const marketType = market?.description.marketType;
  const isDisabledLayOdds = isGame ? isGamesLayOddsDisabled : isSportsDisabledLayOdds;
  const marketPrice = getBestPrices({ marketPrices, ...bet });
  const bestPrice = getBestPriceFromMarketPrices(marketPrice, marketType ?? '', bettingType ?? '');
  const marketCurrency = marketPrices?.currency;
  const isCurrencyChanged =
    isLoggedIn && !loadingUser && !!marketCurrency && !!userCurrency && userCurrency !== marketCurrency;
  const currentPrice = !isConfirmationStep && autoRefreshIsEnabled && isPNCEnabled && !isGame ? bestPrice : priceValue;
  const { isDisabled } = useIsMobilePlacementDisabled(bet);
  const isPrevOddsLower = !isDisabled && !!(bet.price && +bet.price < (bestPrice || 0));
  const isGoingUp = !isDisabled && isBack ? isPrevOddsLower : !isPrevOddsLower;
  const isOddsChangedIndicatorsEnabled =
    isPNCEnabled &&
    autoRefreshIsEnabled &&
    !isGame &&
    !!bet.price &&
    !!bestPrice &&
    bet.price !== bestPrice &&
    !isDisabled;
  const showOddsChangedIndicators = isOddsChangedIndicatorsEnabled && !isConfirmationStep;
  const isEditActionParam = searchParams.get('action') === 'edit';
  const offerIdFromParams = Number(searchParams.get('offerId'));
  const isClosed = marketStatus === MarketStatuses.CLOSED;
  const placementTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stringifiedMarketDefinition = JSON.stringify(marketPrices?.marketDefinition);
  const lineRangeInfo = market?.description?.lineRangeInfo;
  const marketUnit = lineRangeInfo?.marketUnit || 'runs';
  const priceLadderDescription = market?.description.priceLadderDescription;
  const isBetslipVisible = !(
    (status === InlinePlacementStatuses.PLACED || bet.action !== InlinePlacementActions.EDIT) &&
    placedBet
  );

  const payout = calculatePayout(currentPrice, sizeValue, {
    marketType,
    bettingType,
    eachWayDivisor: bet.eachWayDivisor,
    betType: bet.type
  });

  const isInfoError =
    invalidInputs[BetFocusFields.PRICE] &&
    isAmericanOdds &&
    !isLineMarket &&
    errorMessage == t(VALIDATION_ERROR_BET_INVALID_US_PRICE);

  useMobilePlacementNotificationMatchedBySystem({
    bet: currentBet,
    pageBlock,
    onMatched: () => dispatch(removeInlineSelectedBet(bet))
  });

  const setProfitValidation = (validation: TProfitValidation, price: TPrice = currentPrice) => {
    const calculatedSize = calculateSize(price, validation.validValue, {
      bettingType: bet.bettingType,
      useFloorRounding: true
    });

    setSizeValue(calculatedSize);
    setCurrentFocus(BetFocusFields.SIZE);

    if (validation.errorMessage) {
      setErrorMessage(t(validation.errorMessage.text, validation.errorMessage.params));
    }
  };

  const validateProfitValue = () => {
    const validation = validateProfit({ profit, currency, defaultCurrency, isGame });

    if (!validation.isValid) {
      setProfitValidation(validation);
    }
  };

  const calculateLiability = (size: TSize = sizeValue) => {
    return getLiabilityValue({
      marketId: bet.marketId,
      type: bet.type,
      handicap: bet.handicap,
      selectionId: bet.selectionId,
      priceValue: currentPrice,
      sizeValue: size,
      marketDefinition: marketPrices?.marketDefinition,
      eachWayDivisor: bet.eachWayDivisor
    });
  };

  const calculateProfit = () => {
    if (isLineMarket) return sizeValue; // For Back Line market liability equals stake

    return formatFloatNumber(Number(payout) - Number(calculateLiability() || 0));
  };

  /**
   * Recalculate profit/liability when price or size is updated
   */
  useEffect(() => {
    if (isBack) {
      setProfit(calculateProfit());
    } else {
      if (currentFocus !== BetFocusFields.LIABILITY) {
        setLiabilityValue(calculateLiability());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceValue,
    sizeValue,
    currentPrice,
    isLineMarket,
    stringifiedMarketDefinition,
    isLay,
    bet.marketId,
    bet.selectionId,
    bet.handicap,
    bet.type,
    bet.eachWayDivisor
  ]);

  useEffect(() => {
    if (priceValue !== bet.price) {
      setPriceValue(bet.price);
      setStatus(InlinePlacementStatuses.NEW);
    }
  }, [bet.price]);

  useEffect(() => {
    if (elRef.current && shouldScrollToView) {
      setTimeout(() => {
        elRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }, [shouldScrollToView]);

  useEffect(() => {
    const isCancelling =
      currentBet &&
      (currentBet.action == CurrentBetActions.CANCELLING || currentBet.action == CurrentBetActions.CANCELLING_ALL);
    const isEditing = currentBet && currentBet.action == CurrentBetActions.EDITING;

    if (
      currentBet &&
      (isCancelling || (isEditing && currentOldBets.length !== 0)) &&
      currentBet.offerState == BetsStatusesTypes.CANCELLED
    ) {
      setPlacedBet(currentBet);
      setStatus(InlinePlacementStatuses.PLACED);
    }
  }, [currentBet, currentOldBets]);

  const removeBet = (isManualClicked?: boolean) => {
    dispatch(removeInlineSelectedBet(bet));

    if (isEditActionParam && offerIdFromParams && isManualClicked) {
      searchParams.delete('action');
      searchParams.delete('offerId');

      setSearchParams(searchParams, { replace: true });
    }
  };

  useEffect(() => {
    if (placedBet) {
      setPersistenceType(placedBet.persistenceType);
    }
  }, [placedBet]);

  useEffect(() => {
    const newPrice = getBestPriceFromMarketPrices(marketPrice, marketType ?? '', bettingType ?? '');

    if (newPrice != priceValue && bestPrice != newPrice) {
      if (isTakeOffer) {
        setPrevPriceValue(priceValue);
        setPriceValue(newPrice);
      }
    }
  }, [bettingType, isTakeOffer, marketPrice, marketType]);

  useEffect(() => {
    const currentOfferId = inlineSelectedBet?.currentOfferId;

    if (
      ([InlinePlacementStatuses.PROGRESS, InlinePlacementStatuses.TIMEOUT_HIDDEN] as InlinePlacementStatus[]).includes(
        status
      ) &&
      currentOfferId &&
      inlineSelectedBet.offers &&
      inlineSelectedBet.offers[currentOfferId] &&
      (
        [BetsStatusesTypes.MATCHED, BetsStatusesTypes.PLACED, BetsStatusesTypes.CANCELLED] as BetsStatusesType[]
      ).includes(inlineSelectedBet.offers[currentOfferId].offerState)
    ) {
      setPlacedBet(inlineSelectedBet.offers[currentOfferId]);
      setStatus(InlinePlacementStatuses.PLACED);
    }
  }, [inlineSelectedBet]);

  useEffect(() => {
    if (status === InlinePlacementStatuses.PROGRESS) {
      placementTimeout.current = setTimeout(() => {
        setErrorMessage(t(LOADING_TIMEOUT_MESSAGE));
        setStatus(InlinePlacementStatuses.TIMEOUT_HIDDEN);
      }, +betslipSpinnerTime * 1000);
    }

    if (status === InlinePlacementStatuses.PLACED && !hasErrorMessage) {
      onClosingBetslip(true);
      removeBet();
    }

    return () => {
      if (placementTimeout.current) {
        clearTimeout(placementTimeout.current);
        placementTimeout.current = null;
      }
    };
  }, [status, bet.action]);

  const handleChangeInvalidInputs = (input: Exclude<BetFocusField, 'LIABILITY'>, value: boolean) => {
    setInvalidInputs(prevState => ({
      ...prevState,
      [input]: value
    }));
  };

  const validateSizeValue = (newSizeValue: TSize) => {
    const { isValid } = validateSize({
      size: newSizeValue,
      betType: bet.type,
      currency,
      defaultCurrency,
      currencyCode: bet.currency,
      isOperatorBettingLimitsEnabled: false,
      isGame
    });

    if (isSizeValid !== isValid) {
      setIsSizeValid(isValid);
    }
  };

  const handleUpdateSizeValue = (newSizeValue: TSize) => {
    setSizeValue(newSizeValue);
    validateSizeValue(newSizeValue);
  };

  useEffect(() => {
    dispatch(
      setInlinePlacedSize({
        betUuid: bet.betUuid,
        price: priceValue || 0,
        size: sizeValue || 0,
        marketId: bet.marketId,
        pageBlock
      })
    );

    validateSizeValue(sizeValue);
  }, [priceValue, sizeValue]);

  useEffect(() => {
    setPriceValue(bet.price);
    if (!bet.size && accountSettings?.defaultStake && !isGame && defStakeEnabled) {
      const defStake = accountSettings?.defaultStakes.find(item => item?.defaultValue);
      handleUpdateSizeValue(defStake?.value ?? '');
    } else {
      handleUpdateSizeValue(bet.size ?? '');
    }
    if (bet.currentOfferId) {
      if (bet.offers) {
        setPlacedBet(bet.offers[bet.currentOfferId]);
      }
      setStatus(bet.action ? InlinePlacementStatuses.PLACED_EDIT : InlinePlacementStatuses.PLACED);
    } else {
      setStatus(InlinePlacementStatuses.NEW);
      setPlacedBet(null);
    }
  }, [bet.selectionId, bet.type, bet.action]);

  useEffect(() => {
    return () => {
      onClosingBetslip(false);
      dispatch(removeInlineSelectedBet(bet));
      dispatch(setIsMobilePlacementConfirmationStep(false));
    };
  }, []);

  useEffect(() => {
    if (isDisabledLayOdds) {
      dispatch(removeInlineSelectedBet(bet));
    }
  }, [isDisabledLayOdds]);

  const handleValidateSizeInput = (isLiabilityField?: boolean) => {
    const {
      isValid,
      validValue,
      errorMessage: sizeErrorMessage
    } = validateSize({
      size: sizeValue,
      betType: bet.type,
      currency,
      defaultCurrency,
      isOperatorBettingLimitsEnabled: false,
      isGame,
      isLiabilityField
    });

    if (!isValid) {
      if (isBack) {
        const profitValidation = validateProfit({ profit, currency, defaultCurrency, isGame });

        if (!profitValidation.isValid) {
          const calculatedSize = calculateSize(priceValue, profitValidation.validValue, {
            bettingType: bet.bettingType,
            useFloorRounding: true
          });

          if (Number(calculatedSize) < Number(validValue)) {
            setProfitValidation(profitValidation);
            return;
          }
        }
      }

      handleChangeInvalidInputs(BetFocusFields.SIZE, true);

      if (sizeErrorMessage) {
        setErrorMessage(t(sizeErrorMessage.text, sizeErrorMessage.params));
      }

      setSizeValue(validValue);
      setLiabilityValue(calculateLiability(validValue));

      if (!isSizeValid) {
        setIsSizeValid(true);
      }
    } else if (isBack) {
      validateProfitValue();
    }
  };

  const handleDirtySize = () => {
    if (!bet.isDirtySize) {
      dispatch(
        updateInlineSelectedBet({
          betUuid: bet.betUuid,
          pageBlock: bet.pageBlock,
          marketId: bet.marketId,
          isDirtySize: true
        })
      );
    }
  };

  const handlerQuickBets = useCallback(
    (value: string | number) => {
      const newSizeValue = precisionFormat(+(sizeValue || 0) + +value);
      handleUpdateSizeValue(newSizeValue);
      handleDirtySize();
      setCurrentFocus(BetFocusFields.SIZE);
    },
    [sizeValue]
  );

  const onChangeLiability = (newLiability: string | number) => {
    const newSizeValue = calculateSize(priceValue, newLiability, { bettingType: bet.bettingType });

    setLiabilityValue(newLiability);
    setSizeValue(newSizeValue);
  };

  const getOddsTooltipData = () => {
    if (isConfirmationStep) {
      if (isLineMarket) {
        const key = isLay ? BETSLIP_BACKERS_UNITS : BETSLIP_UNITS;
        return { key, options: { UNITS: marketUnit.toLocaleLowerCase() } };
      }

      return { key: isLay ? BETSLIP_BACKERS_ODDS : BETSLIP_ODDS };
    }
  };

  const resetErrorMessage = () => {
    setErrorMessage('');
    setInvalidInputs(initialInvalidInputs);
  };

  const getLiabilityInputValue = () => {
    if (liabilityValue === 0) {
      return !bet.isDirtySize && !bet.size ? '' : 0;
    }

    return liabilityValue;
  };

  const stakeTooltipKey = isLay ? BETSLIP_BACKERS_STAKE : BETSLIP_STAKE;
  const bestPriceFormatted = formatBestPrice(bestPrice, bet.marketType, bet.bettingType ?? '');

  const renderPlaceButton = () => {
    return (
      <PlaceButton
        bet={bet}
        page={page}
        pageBlock={pageBlock}
        profit={profit}
        placedBet={placedBet}
        sizeValue={sizeValue}
        persistenceType={persistenceType}
        priceValue={priceValue}
        prevPriceValue={prevPriceValue}
        isSizeValid={isSizeValid}
        isTakeOffer={isTakeOffer}
        isPriceReady={isPriceReady}
        setErrorMessage={setErrorMessage}
        setIsTakeOffer={setIsTakeOffer}
        setHasErrorMessage={setHasErrorMessage}
        setStatus={setStatus}
        setPriceValue={setPriceValue}
      />
    );
  };

  if (isClosed) {
    return <MobilePlacementClosedMessage bet={bet} />;
  }

  return (
    <>
      <div
        ref={elRef}
        className={classNames(
          containerClassName,
          bet.type === BetSides.Back ? branding.BACK : branding.LAY,
          branding.MOBILE_BETSLIP,
          { [branding.DISABLED]: isDisabled }
        )}
      >
        {isBetslipVisible && (
          <>
            <MobileBetslipHeader bet={bet} pageBlock={pageBlock} removeBet={removeBet} />
            {isCurrencyChanged ? (
              <div className={styles.currencyChangedWrapper}>
                <MobilePlacementValidationMessage
                  message={t(VALIDATION_ERROR_DIFFERENT_CURRENCY_MARKET, { currency_ISO_code: marketCurrency ?? '' })}
                  type="info"
                />
              </div>
            ) : (
              <div
                className={classNames('biab_open-bet-details', styles.betDetails, {
                  [styles.betDetails__disabled]: isDisabled
                })}
              >
                <div className={classNames({ [styles.landscape__top]: isLandscape })}>
                  <div
                    className={classNames(styles.inputsWrapper, {
                      [styles.inputsWrapper__landscape]: isLandscape
                    })}
                  >
                    {(isPNCEnabled && !isGame) || isConfirmationStep || isDisabled ? (
                      <NoEditableInput
                        value={convertOdds(currentPrice || '', isLineMarket ? OddsTypes.DEC : oddsType)}
                        label={isLineMarket && !isPNCEnabled ? t('market.units.runs') : t('betslip.labels.price.back')}
                        showOddsChangedIndicators={showOddsChangedIndicators}
                        isGoingUp={isGoingUp}
                        isLandscape={isLandscape}
                        tooltipData={getOddsTooltipData()}
                        id={MOBILE_INPUT_PRICE}
                      />
                    ) : (
                      <PriceInput
                        setErrorMessage={setErrorMessage}
                        setPriceValue={setPriceValue}
                        customPriceValue={customPriceValue}
                        priceLadderDescription={priceLadderDescription}
                        lineRangeInfo={lineRangeInfo}
                        marketType={marketType || ''}
                        bettingType={bettingType || ''}
                        betType={bet.type}
                        marketUnit={marketUnit}
                        priceValue={priceValue || ''}
                        currentFocus={currentFocus}
                        setIsPriceReady={setIsPriceReady}
                        setFocused={() => setCurrentFocus(BetFocusFields.PRICE)}
                        isInvalid={invalidInputs[BetFocusFields.PRICE] && !isInfoError}
                        onChangeInvalidInputs={handleChangeInvalidInputs}
                        profit={profit}
                        currency={currency}
                        setProfitValidation={setProfitValidation}
                        onValidBlur={() => isBack && validateProfitValue()}
                        isGame={isGame}
                      />
                    )}
                    {isConfirmationStep || isDisabled ? (
                      <NoEditableInput
                        value={sizeValue}
                        label={t(
                          isAmericanOdds ? `betslip.labels.${isBack ? 'toBet' : 'toWin'}` : 'betslip.labels.stake'
                        )}
                        isLandscape={isLandscape}
                        tooltipData={isConfirmationStep ? { key: stakeTooltipKey } : null}
                        id={MOBILE_INPUT_SIZE}
                      />
                    ) : (
                      <SizeInput
                        setSizeValue={newSize => {
                          handleUpdateSizeValue(newSize);
                          handleDirtySize();
                        }}
                        sizeValue={sizeValue ?? ''}
                        currentFocus={currentFocus}
                        setFocused={() => setCurrentFocus(BetFocusFields.SIZE)}
                        isInvalid={invalidInputs[BetFocusFields.SIZE]}
                        validateSizeInput={handleValidateSizeInput}
                        currency={currency}
                        isBack={isBack}
                        isGame={isGame}
                      />
                    )}
                  </div>
                  <div
                    className={classNames(styles.payoutAndLiability, {
                      [styles.payoutAndLiability__landscape]: isLandscape
                    })}
                  >
                    {!isLandscape && <Payout payout={payout} isLandscape={false} />}
                    {isLay ? (
                      <>
                        {isConfirmationStep || isDisabled ? (
                          <NoEditableInput
                            value={liabilityValue}
                            label={t(`betslip.labels.${isAmericanOdds ? 'toBet' : 'liability'}`)}
                            isLandscape={isLandscape}
                            tooltipData={isConfirmationStep ? { key: BETSLIP_LIABILITY } : null}
                            id={MOBILE_INPUT_LIABILITY}
                          />
                        ) : (
                          <LiabilityInput
                            label={t(`betslip.labels.${isAmericanOdds ? (isBack ? 'toWin' : 'toBet') : 'liability'}`)}
                            onChangeLiability={onChangeLiability}
                            liabilityValue={getLiabilityInputValue() ?? ''}
                            sizeValue={sizeValue}
                            currentFocus={currentFocus}
                            setFocused={() => setCurrentFocus(BetFocusFields.LIABILITY)}
                            validateSizeInput={handleValidateSizeInput}
                            isGame={isGame}
                          />
                        )}
                      </>
                    ) : (
                      <Liability
                        label={t(`betslip.labels.${isAmericanOdds ? (isBack ? 'toWin' : 'profit') : 'profit'}`)}
                        liability={profit ?? 0}
                        isLandscape={isLandscape}
                        isConfirmationStep={isConfirmationStep}
                      />
                    )}
                  </div>
                  {isLandscape && renderPlaceButton()}
                </div>
                {placedBet?.persistenceEnabled && (
                  <PersistenceRadioGroup
                    bet={bet}
                    persistenceType={persistenceType}
                    setPersistenceType={setPersistenceType}
                    pageBlock={pageBlock}
                  />
                )}
                {errorMessage && (
                  <MobilePlacementValidationMessage
                    message={errorMessage}
                    type={isInfoError ? 'info' : 'warning'}
                    resetErrorMessage={resetErrorMessage}
                    className={classNames({ [styles.landscape__validationMsg]: isLandscape })}
                  />
                )}
                {showOddsChangedIndicators && (
                  <div
                    className={classNames(
                      styles.oddsChangedNotification,
                      mobileComponents.NOTIFICATION,
                      mobileComponents.INFO,
                      {
                        [styles.oddsChangedNotification__landscape]: isLandscape,
                        [styles.oddsChangedNotification__marginTop]: !!errorMessage
                      }
                    )}
                  >
                    <div className={styles.oddsChangedNotification__inner}>
                      <i
                        className={classNames(
                          'biab_custom-icon-info-circle',
                          styles.oddsChangedNotification__icon,
                          mobileIcons.INFO_ICON
                        )}
                      />
                      <SelectedBetOddsNotificationMessage
                        isLineMarket={isLineMarket}
                        isGoingUp={isGoingUp}
                        odds={bestPriceFormatted}
                        initPrice={bet.price}
                      />
                    </div>
                  </div>
                )}
                <QuickBets
                  handler={handlerQuickBets}
                  isDisabled={
                    isConfirmationStep ||
                    isDisabled ||
                    (currentFocus === BetFocusFields.PRICE && isAmericanOdds && !isLineMarket)
                  }
                  quickBetClassname={styles.quickBet}
                  emptyContainer={isLandscape ? undefined : <div className={styles.emptyQuickBets} />}
                  customClassName={styles.quickBets}
                  shouldIgnoreBlurEvent
                />
                {virtualKeyboardBetslip && (
                  <PlacementVirtualKeyboard
                    priceValue={priceValue}
                    isDisabledKeys={isConfirmationStep || isDisabled}
                    setLiabilityValue={setLiabilityValue}
                    setPriceValue={setPriceValue}
                    customPriceValue={customPriceValue}
                    setCustomPriceValue={setCustomPriceValue}
                    setSizeValue={setSizeValue}
                    handleDirtySize={handleDirtySize}
                    currentFocus={currentFocus}
                    liabilityValue={liabilityValue}
                    sizeValue={sizeValue}
                    isLineMarket={isLineMarket}
                    bet={bet}
                  />
                )}
                {!isLandscape && (
                  <div className={classNames({ [styles.marginTop]: !virtualKeyboardBetslip })}>
                    {renderPlaceButton()}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <RGModalMessage />
    </>
  );
}

export default MobilePlacement;
